if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.isHydrated = this.dataset.shellOnly === 'false';
      this.hydrationPromise = null;
      this.activeElement = null;
      this.openRequestId = 0;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') this.close();
      });
      this.addEventListener('click', (event) => this.handleDrawerClick(event));
      this.setHeaderCartIconAccessibility();
    }

    handleDrawerClick(event) {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('[data-cart-drawer-close], #CartDrawer-Overlay')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-cart-drawer-retry]')) {
        event.preventDefault();
        this.hydrationPromise = null;
        this.showLoading();
        this.ensureHydrated().catch(() => this.showLoadError());
      }
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-expanded', 'false');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        const code = event.code.toUpperCase();
        if (code !== 'SPACE' && code !== 'ENTER' && code !== 'NUMPADENTER') return;
        event.preventDefault();
        this.open(cartLink);
      });
    }

    async open(triggeredBy) {
      this.init();
      if (triggeredBy) this.setActiveElement(triggeredBy);

      const requestId = ++this.openRequestId;
      this.classList.add('animate');
      this.classList.remove('active');
      await this.waitForClosedFrame();
      if (requestId !== this.openRequestId) return;

      this.resetScrollPosition();
      this.classList.add('active');
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('cart-drawer-open');
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'true');
      this.focusDialog();
      publish(PUB_SUB_EVENTS.cartOpen);

      try {
        await this.ensureHydrated();
        this.resetScrollPosition();
        if (this.classList.contains('active')) this.focusDialog();
      } catch (error) {
        console.error('[Cart drawer] Unable to load cart content.', error);
        this.showLoadError();
      }
    }

    close() {
      const wasOpen = this.classList.contains('active');
      this.openRequestId += 1;

      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('cart-drawer-open');
      document.querySelector('#cart-icon-bubble')?.setAttribute('aria-expanded', 'false');
      if (wasOpen) publish(PUB_SUB_EVENTS.cartClose);
    }

    waitForClosedFrame() {
      return new Promise((resolve) => {
        let isSettled = false;
        const finish = () => {
          if (isSettled) return;
          isSettled = true;
          resolve();
        };

        requestAnimationFrame(() => requestAnimationFrame(finish));
        setTimeout(finish, 100);
      });
    }

    focusDialog() {
      const dialog = this.querySelector('.drawer__inner');
      if (dialog) trapFocus(dialog, dialog);
    }

    resetScrollPosition() {
      const scrollContainer = this.querySelector('cart-drawer-items');
      if (scrollContainer) scrollContainer.scrollTop = 0;
    }

    showLoading() {
      this.querySelector('[data-cart-drawer-loading]')?.removeAttribute('hidden');
      this.querySelector('[data-cart-drawer-error]')?.setAttribute('hidden', '');
    }

    showLoadError() {
      this.querySelector('[data-cart-drawer-loading]')?.setAttribute('hidden', '');
      const error = this.querySelector('[data-cart-drawer-error]');
      error?.removeAttribute('hidden');
      error?.querySelector('[data-cart-drawer-fallback]')?.removeAttribute('hidden');
      error?.querySelector('[data-cart-drawer-retry]')?.focus();
    }

    async ensureHydrated(sectionHtml = null) {
      if (this.isHydrated && !sectionHtml) return;
      if (this.hydrationPromise && !sectionHtml) return this.hydrationPromise;

      const hydrate = async () => {
        let html = sectionHtml;
        if (!html) {
          const separator = routes.cart_url.includes('?') ? '&' : '?';
          const response = await fetch(`${routes.cart_url}${separator}section_id=cart-drawer`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error(`Cart section request failed with ${response.status}`);
          html = await response.text();
        }

        await this.renderDrawerSection(html);
        this.isHydrated = true;
        this.dataset.shellOnly = 'false';
      };

      if (sectionHtml) return hydrate();
      this.hydrationPromise = hydrate().catch((error) => {
        this.hydrationPromise = null;
        throw error;
      });
      return this.hydrationPromise;
    }

    parseSection(html) {
      const normalized = html.replace(/<template\b[^>]*>|<\/template>/g, '');
      return new DOMParser().parseFromString(normalized, 'text/html');
    }

    async renderDrawerSection(html) {
      if (!html) throw new Error('Cart drawer section HTML is missing.');

      const sectionDocument = this.parseSection(html);
      const sourceRoot = sectionDocument.querySelector('cart-drawer');
      const sourceDrawer = sectionDocument.querySelector('#CartDrawer');
      const currentDrawer = this.querySelector('#CartDrawer');
      if (!sourceRoot || !sourceDrawer || !currentDrawer) {
        throw new Error('Cart drawer section response is incomplete.');
      }

      const styles = [...sourceRoot.querySelectorAll('link[rel="stylesheet"][href]')];
      const scripts = [...sourceRoot.querySelectorAll('script[src]')];
      await Promise.all(styles.map((link) => this.loadStylesheet(link.href)));
      for (const script of scripts) await this.loadScript(script.src);

      currentDrawer.replaceWith(sourceDrawer);
      this.classList.toggle('is-empty', sourceRoot.classList.contains('is-empty'));
      this.removeDeleteTabIndex();
      window.loadTemplateContent(this);
    }

    loadStylesheet(href) {
      const existing = [...document.head.querySelectorAll('link[rel="stylesheet"][href]')]
        .find((link) => link.href === href);
      if (existing) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = resolve;
        link.onerror = () => reject(new Error(`Unable to load stylesheet: ${href}`));
        document.head.appendChild(link);
      });
    }

    loadScript(src) {
      const existing = [...document.scripts].find((script) => script.src === src);
      if (existing) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.defer = true;
        script.onload = resolve;
        script.onerror = () => reject(new Error(`Unable to load script: ${src}`));
        document.head.appendChild(script);
      });
    }

    async reload() {
      const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
      const separator = routes.cart_url.includes('?') ? '&' : '?';
      try {
        const response = await fetch(`${routes.cart_url}${separator}sections=${sectionIds}`, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`Cart refresh failed with ${response.status}`);
        await this.renderContents({ sections: await response.json() }, true);
      } catch (error) {
        console.error('[Cart drawer] Unable to refresh cart content.', error);
        this.showLoadError();
      }
    }

    async renderContents(parsedState, preventOpen = false) {
      const drawerSection = parsedState?.sections?.['cart-drawer'];
      if (drawerSection) await this.ensureHydrated(drawerSection);

      const bubbleSection = parsedState?.sections?.['cart-icon-bubble'];
      const cartBubble = document.getElementById('cart-icon-bubble');
      if (bubbleSection && cartBubble) {
        const bubbleDocument = this.parseSection(bubbleSection);
        const sourceBubble = bubbleDocument.querySelector('#cart-icon-bubble');
        if (sourceBubble) cartBubble.innerHTML = sourceBubble.innerHTML;
      }

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) await this.open();
    }

    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' },
      ];
    }

    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => button.removeAttribute('tabindex'));
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
